import { z } from 'zod';
import { TimestampSchema } from './common.js';

export const SpecLaunchStateSchema = z.enum(['off', 'read_only', 'on']);
export type SpecLaunchState = z.infer<typeof SpecLaunchStateSchema>;

export const SpecModelClassSchema = z.enum(['smart', 'fast']);
export type SpecModelClass = z.infer<typeof SpecModelClassSchema>;

export const SpecToolDecisionSchema = z.enum(['always_allow', 'needs_approval', 'blocked']);
export type SpecToolDecision = z.infer<typeof SpecToolDecisionSchema>;

export const SpecContextModeSchema = z.enum(['live', 'pinned']);
export type SpecContextMode = z.infer<typeof SpecContextModeSchema>;

export const SpecRunStatusSchema = z.enum([
  'created',
  'reserving',
  'running',
  'awaiting_approval',
  'completing',
  'completed',
  'failed',
  'canceled',
  'funds_exhausted',
  'policy_blocked',
]);
export type SpecRunStatus = z.infer<typeof SpecRunStatusSchema>;

/**
 * Longest user-supplied conversation title the runtime stores.
 *
 * Shared because three surfaces validate it: the Worker's thread routes, the
 * API's conversation routes, and the API's `normalizeTitle`. When the two
 * outer ones allowed 1000 and the inner one allowed 200, a title in between
 * passed validation twice and then failed at the bottom.
 *
 * Distinct from the generated-title budget in `apps/spec/src/title.ts`, which
 * bounds what the fast model may produce, not what a user may send.
 */
export const SPEC_TITLE_MAX_LENGTH = 200;

/**
 * The label and reasoning profile a class's model row must carry.
 *
 * Defined once here because FOUR places enforce this and every one of them is
 * a place a run can die: the API's `resolveModelConfig` (which pins the row a
 * run reserves against), the private model-config route, and the Worker's
 * `api-client` and `model-gateway`. Each time a rule lived in only some of
 * them, `beginRun` committed the user message and reserved credits against a
 * row a later gate refused, and the run failed after spending. `provider` and
 * `label` drifted that way once; `profile` drifted the same way immediately
 * after. A shared definition is the only version that cannot drift again.
 */
export function expectedSpecModelLabel(modelClass: SpecModelClass): string {
  return modelClass === 'smart' ? 'Spec 1.0' : 'Spec 1.0 fast';
}

/**
 * Labels a STORED row may carry and still be served, as opposed to the one
 * label a NEW row is issued with.
 *
 * These are different questions and the code answered only the second, which is
 * what made the rename a deploy-time break rather than a rename. Production
 * migrates BEFORE `deploy-api` and `deploy-spec` (`ci.yml`: both `needs:
 * migrate`), so there is a window in which the database has already moved and
 * the OLD binaries are still serving. Any change that makes the two disagree
 * fails every run inside that window -- and with a single accepted label,
 * renaming makes them disagree in one direction or the other no matter which
 * side moves first.
 *
 * The rename therefore shipped as an expand/contract. The EXPAND half (PR 12.5)
 * had every binary accept both spellings, which made the database's own state
 * irrelevant to the rollout. This is the CONTRACT half: once the expand had
 * fully deployed, `infra/supabase/migrations/068_spec_model_label_rollout.sql`
 * retired the legacy rows and this narrowed back to one label per class.
 *
 * It stays at one. The pre-rename spellings are refused now, and a rename that
 * needs an alias window has to repeat the expand/contract rather than leave one
 * standing. A new logical model gets a new `SpecModelClass`, never another
 * alias for an existing one.
 */
export function acceptedSpecModelLabels(modelClass: SpecModelClass): readonly string[] {
  return [expectedSpecModelLabel(modelClass)];
}

/** Whether a stored row's label identifies this logical model. */
export function isAcceptedSpecModelLabel(modelClass: SpecModelClass, label: string): boolean {
  return acceptedSpecModelLabels(modelClass).includes(label);
}

/** The provider a web_search step's receipt names. */
export const SPEC_WEB_PROVIDER = 'parallel';

/**
 * Truncate to at most `maxUnits` UTF-16 code units without splitting a
 * surrogate pair.
 *
 * Every Spec text budget is expressed in code units, so the cut point can land
 * between the two halves of an astral character (an emoji, or most non-BMP
 * script). A plain `slice` emits the orphaned half, which renders as a
 * replacement character and corrupts whatever is being bounded. Worse on the
 * API side: `JSON.stringify` writes the orphan as `\ud83c`, and Postgres
 * refuses that text as `jsonb` ("Unicode low surrogate must follow a high
 * surrogate"), so a single clamped label can abort the transaction that stores
 * a message. Dropping the orphan keeps the budget intact and costs at most one
 * unit.
 *
 * Lives here rather than in either Worker because both the Spec Worker's prompt
 * budgets and the API's frozen-context labels clamp attacker-influenced text,
 * and two copies of this rule is how one of them silently stops applying it.
 */
export function sliceCodeUnits(text: string, maxUnits: number): string {
  if (maxUnits <= 0) return '';
  if (text.length <= maxUnits) return text;
  const cut = text.slice(0, maxUnits);
  const last = cut.charCodeAt(cut.length - 1);
  const isLoneHighSurrogate = last >= 0xd800 && last <= 0xdbff;
  return isLoneHighSurrogate ? cut.slice(0, -1) : cut;
}

/**
 * Every reason a run can end other than cleanly.
 *
 * Shared here rather than in the Worker that raises them because the web app
 * has to turn each one into a sentence, and a vocabulary only one side knows
 * is one the other side handles by guessing.
 */
export const SPEC_RUN_ERROR_CODES = [
  'provider_error',
  'provider_timeout',
  'provider_refused',
  'provider_overloaded',
  'output_limit',
  'context_overflow',
  'canceled_by_user',
  'client_disconnected',
  'funds_exhausted',
  'payment_required',
  'policy_blocked',
  'model_unavailable',
  'run_expired',
  'run_abandoned',
  'lease_lost',
  'internal_error',
] as const;

export type SpecRunErrorCode = (typeof SPEC_RUN_ERROR_CODES)[number];

const SPEC_RUN_ERROR_CODE_SET = new Set<string>(SPEC_RUN_ERROR_CODES);

export function isSpecRunErrorCode(value: unknown): value is SpecRunErrorCode {
  return typeof value === 'string' && SPEC_RUN_ERROR_CODE_SET.has(value);
}

/**
 * Wall-clock ceiling on a single run. The coordinator clamps its own deadline
 * to `min(now + this, reservationExpiresAt)`, so a run always ends before its
 * funding could lapse unowned.
 *
 * Shared here rather than in either app because the API mints the run token
 * against it and the Worker schedules its reap alarm against it. When the two
 * drifted, the token expired at the same instant the reap fired.
 */
export const SPEC_RUN_DEADLINE_MS = 600_000;

/**
 * How long a run token stays valid past the run deadline.
 *
 * The reap that settles an abandoned run happens AT the deadline and must
 * authenticate, so the credential has to outlive the deadline by enough to
 * finish `finalizeRun`. Sizing this to zero, or to the reservation expiry the
 * deadline is itself clamped to, wedges the run unsettled.
 */
export const SPEC_RUN_TOKEN_GRACE_MS = 120_000;

/**
 * Expiry for a run token minted at `now` for a run funded until
 * `reservationExpiresAt`.
 *
 * Deliberately NOT clamped to the reservation expiry: the whole point of the
 * grace is to still be able to settle a reservation that has just lapsed.
 *
 * Floored at `now` for the same reason. A retried send replays the STORED
 * reservation, so a retry that arrives after the original TTL passes an expiry
 * already in the past; without the floor the mint produced a token that was
 * expired at issue, the coordinator could not authenticate any private write,
 * and the run never reached a terminal status -- leaving `beginRun` refusing
 * every later message on that conversation as `spec_run_active`, permanently.
 * A lapsed reservation still needs settling or releasing, and the grace is
 * exactly the window for it.
 */
export function specRunTokenExpiry(now: number, reservationExpiresAt?: string | null): number {
  const deadline = now + SPEC_RUN_DEADLINE_MS;
  const parsed = reservationExpiresAt == null ? Number.NaN : Date.parse(reservationExpiresAt);
  const bounded = Number.isFinite(parsed) ? Math.min(deadline, parsed) : deadline;
  return Math.max(bounded, now) + SPEC_RUN_TOKEN_GRACE_MS;
}

/** A credit is represented internally in millionths of a credit. */
export const SpecCreditMicrosSchema = z
  .number()
  .finite()
  .int()
  .nonnegative()
  .max(Number.MAX_SAFE_INTEGER);
export type SpecCreditMicros = z.infer<typeof SpecCreditMicrosSchema>;

/** Parse a value at a boundary where fractional or unsafe credit amounts are invalid. */
export function parseSpecCreditMicros(value: unknown): SpecCreditMicros {
  return SpecCreditMicrosSchema.parse(value);
}

/**
 * How long purchased credits last, in months.
 *
 * Shared because two sides state it: the API writes a purchase grant's
 * `expires_at` exactly this far out, and the buy dialog discloses the date
 * BEFORE the charge (design section 4a). Two copies drift into a dialog that
 * promises one date and a grant that carries another, and on a balance
 * someone paid for that is the worst disagreement in the product.
 */
export const SPEC_PURCHASED_CREDIT_TERM_MONTHS = 12;

/**
 * Defensive bounds on ONE credit purchase, not product policy.
 *
 * The floor sits above Stripe's own minimum USD charge so a too-small amount
 * is a Parcel 400 with a reason rather than a Stripe error surfaced as a 500,
 * and one dollar is the smallest amount that buys a whole credit. The ceiling
 * is the same kind of guard as `MAX_EXTRA_SEATS`: it keeps a mistyped amount
 * from becoming a five-figure charge, and a workspace that genuinely wants
 * more can buy twice.
 *
 * Shared with the buy dialog for the same reason `MAX_SPEC_SEAT_QUANTITY` is:
 * a client copy that drifts low hides a raised server bound, and one that
 * drifts high sends requests that can only 400.
 */
export const SPEC_CREDIT_PURCHASE_MIN_CENTS = 100;
export const SPEC_CREDIT_PURCHASE_MAX_CENTS = 1_000_000;

/** One credit is $1.00: what a Stripe purchase grants and what the app shows. Every dollar-credit conversion reads these. */
export const SPEC_MICROS_PER_CREDIT = 1_000_000;
export const SPEC_CENTS_PER_CREDIT = 100;
export const SPEC_MICROS_PER_CENT = SPEC_MICROS_PER_CREDIT / SPEC_CENTS_PER_CREDIT;

/**
 * The ceiling on a workspace spend limit, in microcredits: 100,000 credits,
 * which is $100,000 of consumption in one period.
 *
 * The spend limit is the mandatory companion to auto-reload, so it is the one
 * number standing between a mistyped field and an unbounded run of automatic
 * charges. A named constant rather than a pricing-table read: the old bound
 * came from the retired postpaid cap columns, and a ceiling on prepaid
 * consumption has nothing to do with what an overage cap used to allow.
 *
 * Shared for the same reason as the purchase bounds above: the route's
 * `parseSpecSpendLimitBody` rejects anything higher, and the limit dialog on
 * Workspace > Billing refuses to send a number the route could only 400.
 *
 * A null limit stays legal and stays unbounded; clearing one is forbidden only
 * while auto-reload is enabled.
 */
export const SPEC_SPEND_LIMIT_MAX_MICROS = 100_000_000_000;

/**
 * What `POST /workspaces/:workspaceId/spec/credits/checkout` returns.
 *
 * The URL and nothing else. The expiry the dialog discloses is computed from
 * `SPEC_PURCHASED_CREDIT_TERM_MONTHS`, so no field here can disagree with the
 * grant the webhook later writes, and no credit figure is returned before a
 * payment that has not happened yet.
 */
export const SpecCreditCheckoutResponseSchema = z.object({ checkoutUrl: z.string().url() });
export type SpecCreditCheckoutResponse = z.infer<typeof SpecCreditCheckoutResponseSchema>;

export const SpecEntitlementKindSchema = z.enum(['free_monthly', 'trial', 'ai_seats']);
export type SpecEntitlementKind = z.infer<typeof SpecEntitlementKindSchema>;

export const SpecUsageSummarySchema = z.object({
  entitlementKind: SpecEntitlementKindSchema,
  includedGranted: SpecCreditMicrosSchema,
  includedUsed: SpecCreditMicrosSchema,
  includedRemaining: SpecCreditMicrosSchema,
  /**
   * Credit BALANCE remaining, which is a different thing from the allowance
   * above and must never be summed with it.
   *
   * The allowance is what an AI seat entitles a workspace to: chat, metered
   * against a limit that renews and expires with the period. A balance is
   * money the workspace owns. It funds everything that is not chat, plus the
   * chat that overflows the allowance, and it survives the period boundary.
   * Presenting one number for both invites the three questions the split
   * exists to answer: whether the allowance rolls over, whether it can be
   * cashed out, and whether it can be bought directly. It does not, it cannot,
   * and it cannot.
   *
   * Two sources feed it: `manual_adjustment`, which the ledger was always
   * willing to spend while the summary never reported it (a support-granted
   * credit, spendable and invisible at the same time), and `purchase`, the
   * credits a workspace buys. `ledger.ts` `balanceSource` is the same pair,
   * stated for the funding side.
   */
  purchasedRemaining: SpecCreditMicrosSchema,
  periodStart: TimestampSchema.nullable(),
  periodEnd: TimestampSchema.nullable(),
  /**
   * Microcredits of purchased-credit budget left in the current spend-limit
   * period. Null means no limit is set, which is the common case (auto-reload
   * off) and is legal.
   *
   * Optional as well as nullable, unlike every other field on this schema, and
   * deliberately so. Web and API deploy from the same merge but not
   * atomically, so a new web bundle can hold a response from an API build that
   * predates this field. Reading an absent limit as "no limit" only lets a
   * member send a message the server then refuses with a clear error; it does
   * not lock anyone out the way reading an absent `purchasedRemaining` as zero
   * would. Fail open here.
   */
  spendLimitRemainingMicros: SpecCreditMicrosSchema.nullish(),
  /**
   * When the spend-limit budget above resets. The SAME window
   * `specUsagePeriodConsumption` reports as `endsAt`, never a second
   * derivation of "the current period" -- see that function's docs for why
   * there used to be more than one and what it cost.
   *
   * Optional as well as nullable, for the same version-skew reason as
   * `spendLimitRemainingMicros` above.
   */
  spendLimitResetsAt: TimestampSchema.nullish(),
});
export type SpecUsageSummary = z.infer<typeof SpecUsageSummarySchema>;

/**
 * The ten values `usage.spec_credit_ledger.category`'s check constraint
 * permits, in display order.
 *
 * Model calls write `ai_inference` and web search writes `web_search`. This
 * array is what turns the ledger's rows into a breakdown that tells a customer
 * what the currency is for: the breakdown shaper (`shapeSpecUsageBreakdown`) is
 * driven from this list rather than from whatever Postgres returns, so every
 * category always appears, zero-valued ones included.
 *
 * Order differs from the check constraint's own order, which is incidental
 * (creation order); this one is the display order.
 */
export const SPEC_CREDIT_CATEGORIES = [
  'ai_inference',
  'agent',
  'web_search',
  'data_enrichment',
  'people_search',
  'company_search',
  'contact_email',
  'contact_profile',
  'contact_phone',
  'company_profile',
] as const;
export type SpecCreditCategory = (typeof SPEC_CREDIT_CATEGORIES)[number];

/** The categories enrichment charges; only these may draw the monthly enrichment grant. */
export const ENRICH_CREDIT_CATEGORIES = [
  'people_search',
  'company_search',
  'contact_email',
  'contact_profile',
  'contact_phone',
  'company_profile',
] as const satisfies readonly SpecCreditCategory[];
export type EnrichCreditCategory = (typeof ENRICH_CREDIT_CATEGORIES)[number];

export function isEnrichCreditCategory(value: unknown): value is EnrichCreditCategory {
  return (
    typeof value === 'string' && (ENRICH_CREDIT_CATEGORIES as readonly string[]).includes(value)
  );
}

/** Parallel's list prices in micro-USD, checked on parallel.ai/pricing 2026-10-11; re-check before any price change. */
export const PARALLEL_PRICING = {
  searchMicrousd: { turbo: 1_000, fast: 1_000, basic: 5_000, advanced: 5_000 },
  extractPerUrlMicrousd: 1_000,
} as const;
export type ParallelSearchMode = keyof typeof PARALLEL_PRICING.searchMicrousd;

/** Spec's web_search searches in this mode; code pins it, the model never picks it. */
export const SPEC_WEB_SEARCH_MODE: ParallelSearchMode = 'fast';
export type SpecWebOperation = 'search' | 'read';

/** One web_search call's list price: a search in the pinned mode, or one page read. */
export function specWebCallListPriceMicrousd(operation: SpecWebOperation): number {
  return operation === 'search'
    ? PARALLEL_PRICING.searchMicrousd[SPEC_WEB_SEARCH_MODE]
    : PARALLEL_PRICING.extractPerUrlMicrousd;
}

export const SpecUsageBreakdownSchema = z.object({
  periodStart: TimestampSchema.nullable(),
  periodEnd: TimestampSchema.nullable(),
  totalMicros: SpecCreditMicrosSchema,
  categories: z.array(
    z.object({ category: z.enum(SPEC_CREDIT_CATEGORIES), usedMicros: SpecCreditMicrosSchema }),
  ),
});
export type SpecUsageBreakdown = z.infer<typeof SpecUsageBreakdownSchema>;

/** `GET /workspaces/:id/spec/enrichment-credits`: this period's enrichment grant and pay-as-you-go spend. */
export const EnrichmentCreditsSummarySchema = z.object({
  plan: z.enum(['free', 'starter', 'pro']),
  periodStart: TimestampSchema,
  periodEnd: TimestampSchema,
  includedMicros: SpecCreditMicrosSchema,
  usedMicros: SpecCreditMicrosSchema,
  remainingMicros: SpecCreditMicrosSchema,
  payAsYouGo: z.array(
    z.object({ category: z.enum(ENRICH_CREDIT_CATEGORIES), usedMicros: SpecCreditMicrosSchema }),
  ),
});
export type EnrichmentCreditsSummary = z.infer<typeof EnrichmentCreditsSummarySchema>;

/**
 * Most AI seats one workspace may purchase.
 *
 * Shared because BOTH sides of the seat-quantity route enforce it: the API's
 * `parseSpecSeatQuantityBody` rejects anything above it, and the web seat
 * control refuses to send a number the route would only 400. Two copies of this
 * bound drift silently -- a lower client copy hides a raised server limit, and a
 * higher one sends requests that can only fail -- and neither side has a test
 * that could notice.
 */
export const MAX_SPEC_SEAT_QUANTITY = 1000;

export const SpecBillingSummarySchema = z.object({
  eligibility: z.enum(['free_grant_only', 'trial', 'ai_addon']),
  purchasedSeatQuantity: z.number().int().nonnegative(),
  pendingSeatQuantity: z.number().int().nonnegative().nullable(),
  assignedSeatCount: z.number().int().nonnegative(),
  assignedMemberIds: z.array(z.string().uuid()),
  seatPriceCents: z.number().int().nonnegative(),
  includedCreditsPerSeatMicros: SpecCreditMicrosSchema,
  pricingVersion: z.string().min(1),
  period: z.object({
    startsAt: TimestampSchema.nullable(),
    endsAt: TimestampSchema.nullable(),
    nextGrantAt: TimestampSchema.nullable(),
  }),
  balance: z.object({
    /** Purchased credits still available. Never negative, and never the allowance. */
    remainingMicros: SpecCreditMicrosSchema,
    /** What a credit costs to buy. 100 at launch, since 1 credit = $1.00. */
    priceCentsPerCredit: z.number().int().nonnegative(),
    /** What of the balance expires, and when. */
    expiring: z.array(z.object({ micros: SpecCreditMicrosSchema, expiresAt: TimestampSchema })),
    autoReload: z.object({
      enabled: z.boolean(),
      thresholdMicros: SpecCreditMicrosSchema,
      amountMicros: SpecCreditMicrosSchema,
      disabledByDeclineAt: TimestampSchema.nullable(),
    }),
    spendLimit: z.object({
      limitMicros: SpecCreditMicrosSchema.nullable(),
      consumedThisPeriodMicros: SpecCreditMicrosSchema,
      resetsAt: TimestampSchema.nullable(),
    }),
    pendingPurchaseCents: z.number().int().nonnegative().nullable(),
  }),
  reconciliation: z.object({
    /**
     * `error` and `stalled` are separate values because the honest copy for
     * them is opposite. `error` is a write the drain is still retrying.
     * `stalled` is a DEAD-LETTERED write: it will never be picked up again, so
     * telling an admin that Parcel keeps retrying is false, and false in the
     * direction that stops them fixing it.
     */
    state: z.enum(['synced', 'pending', 'error', 'stalled']),
    lastErrorCode: z.string().nullable(),
  }),
});
export type SpecBillingSummary = z.infer<typeof SpecBillingSummarySchema>;

/**
 * Whether THIS member may spend the workspace's pooled Spec credits.
 *
 * Included credits pool at the WORKSPACE by design, so `SpecUsageSummary` is a
 * workspace fact and says nothing about who is allowed to draw on it. On a
 * workspace that bills Spec by assigned AI seat, the entitlement service
 * refuses an unassigned member with `SPEC_SEAT_REQUIRED` BEFORE it looks at any
 * balance -- so a summary on its own showed that member a healthy balance and
 * then the server refused their first message.
 *
 * Deliberately one three-valued fact rather than a pair of booleans: with
 * `{required, assigned}` the natural-looking `!assigned` blocks every Free and
 * trial workspace, which hold no seats and need none.
 *
 * - `not_required`: the workspace is not billed by seat (Free grant or trial),
 *   so no assignment gates this member.
 * - `assigned`: the workspace is billed by seat and this member holds one.
 * - `required`: the workspace is billed by seat and this member does not hold
 *   one. Sending is refused; offer a seat instead.
 */
export const SpecMemberSeatStateSchema = z.enum(['not_required', 'assigned', 'required']);
export type SpecMemberSeatState = z.infer<typeof SpecMemberSeatStateSchema>;

export const SpecBootstrapSchema = z.object({
  launchState: SpecLaunchStateSchema,
  workspaceId: z.string().uuid(),
  memberId: z.string().uuid(),
  capabilities: z.object({
    useSpec: z.boolean(),
    manageTools: z.boolean(),
    manageConnections: z.boolean(),
    manageSkills: z.boolean(),
    manageBilling: z.boolean(),
  }),
  modelLabels: z.object({ smart: z.literal('Spec 1.0'), fast: z.literal('Spec 1.0 fast') }),
  usage: SpecUsageSummarySchema,
  memberSeat: SpecMemberSeatStateSchema,
});
export type SpecBootstrap = z.infer<typeof SpecBootstrapSchema>;

/** A trigger level is a finite, non-negative quantity, and a warning must fire below its stop.
    Both zero is the zero-tolerance pair the monitor reads as "any non-zero value stops". */
export function isPublishableSpecThreshold(row: {
  metric: string;
  warningValue: number;
  stopValue: number | null;
}): boolean {
  if (typeof row.metric !== 'string' || !/^[a-z][a-z0-9_]{0,63}$/.test(row.metric)) return false;
  if (!Number.isFinite(row.warningValue) || row.warningValue < 0) return false;
  if (row.stopValue === null) return true;
  if (!Number.isFinite(row.stopValue) || row.stopValue < 0) return false;
  return row.stopValue > row.warningValue || (row.stopValue === 0 && row.warningValue === 0);
}

/** How much of a conversation a run carries: the recent window, the compaction batch, the summary block. */
export const SPEC_THREAD_MEMORY = {
  recentTurnsChars: 24_000,
  olderTurnsMax: 40,
  /** Per-turn ceiling on the compaction batch. Compaction truncates to this anyway,
      so anything longer is payload the Durable Object stores and then discards. */
  olderTurnChars: 800,
  /**
   * Turns that must be pending before a fold is worth a model call.
   *
   * Without it, every send past the recent window pushes one more turn out of
   * it and buys another summary: a metered call on nearly every message. The
   * unfolded turns are not lost meanwhile, they simply wait for the batch to
   * be worth paying for.
   */
  olderTurnsMinBatch: 8,
  historySummaryChars: 6_000,
} as const;

/**
 * Chars-per-token heuristic shared by the Worker's prompt sizing and the API's
 * reservation estimate. One number, because a reservation computed from a
 * different ratio than the prompt it pays for is a shortfall by construction.
 */
export const SPEC_CHARS_PER_TOKEN_ESTIMATE = 4;

/** Rough, provider-agnostic token estimate. Rounds up: an undercount risks a shortfall. */
export function estimateSpecTokenCount(text: string): number {
  return estimateSpecTokensForChars(text.length);
}

/** The same heuristic for callers that already know a length and have no string to measure. */
export function estimateSpecTokensForChars(chars: number): number {
  if (chars <= 0) return 0;
  return Math.max(1, Math.ceil(chars / SPEC_CHARS_PER_TOKEN_ESTIMATE));
}

/** One replayed turn: the text the member or Spec said, without reasoning; a reply's tool steps only as labels. */
export interface SpecThreadTurn {
  id: string;
  sequence: number;
  role: 'user' | 'assistant';
  text: string;
  /** A reply only: the tools it ran, one label each (`query_data (50 rows)`); absent when none. */
  tools?: readonly string[];
}

/** The note the prompt sets ahead of a replayed reply that ran tools, outside Spec's own words. */
export function specToolNote(tools: readonly string[]): string {
  const labels = tools
    .map((label) => label.replace(/[[\]]/g, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  return labels.length === 0
    ? ''
    : `[Parcel's record: the reply that follows ran ${labels.join(', ')}]`;
}

/** What one replayed turn adds to the prompt, in characters: its text and, for a reply, its tool note. */
export function specThreadTurnChars(turn: SpecThreadTurn): number {
  const note = turn.role === 'assistant' ? specToolNote(turn.tools ?? []) : '';
  return turn.text.length + note.length;
}

export interface SpecThreadCompaction {
  boundary: {
    fromSequence: number;
    throughMessageId: string;
    throughSequence: number;
    checksum: string;
  };
  summary: {
    text: string;
    sourceMessageCount: number;
    references: readonly { id: string; sequence: number }[];
  };
}

export interface SpecRunHistory {
  /** Oldest first, all inside the recent window. */
  recent: readonly SpecThreadTurn[];
  /** The member's own URLs across the loaded conversation, newest first: the pages web_search may read. */
  memberUrls?: readonly string[];
  /** Oldest first, above the previous boundary and below the window; empty when nothing is pending. */
  older: readonly SpecThreadTurn[];
  compaction: SpecThreadCompaction | null;
}
